import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken } from "./api";

const Ctx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [meta, setMeta] = useState(null);
  const [offline, setOffline] = useState(!navigator.onLine);

  const loadMeta = useCallback(async () => {
    const { data } = await api.get("/meta");
    setMeta(data);
    localStorage.setItem("fmis_meta", JSON.stringify(data));
  }, []);

  useEffect(() => {
    (async () => {
      if (window.location.hash?.includes("session_id=")) { setUser(false); return; }
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        localStorage.setItem("fmis_user", JSON.stringify(data));
        await loadMeta();
      } catch (e) {
        const cached = localStorage.getItem("fmis_user");
        if (!e.response && cached) {
          setUser(JSON.parse(cached));
          setMeta(JSON.parse(localStorage.getItem("fmis_meta") || "null"));
        } else setUser(false);
      }
    })();
    const out = () => { setUser(false); setMeta(null); };
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener("fmis-logout", out);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("fmis-logout", out);
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, [loadMeta]);

  const login = async (username, password) => {
    const { data } = await api.post("/auth/login", { username, password });
    return finish(data);
  };
  const loginGoogle = async (session_id) => finish((await api.post("/auth/google", { session_id })).data);
  const finish = async (data) => {
    setToken(data.token);
    setUser(data.user);
    localStorage.setItem("fmis_user", JSON.stringify(data.user));
    await loadMeta();
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* offline */ }
    setToken(null);
    localStorage.removeItem("fmis_user");
    localStorage.removeItem("fmis_meta");
    setUser(false);
    setMeta(null);
  };

  const can = (p) => !!meta?.perms?.includes(p);
  const refreshUser = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    localStorage.setItem("fmis_user", JSON.stringify(data));
  };
  const isGlobal = user && GLOBAL_ROLES.includes(user.role);
  return <Ctx.Provider value={{ user, meta, login, loginGoogle, logout, can, isGlobal, offline, reloadMeta: loadMeta, refreshUser }}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);

export const ROLE_LABEL = {
  super_admin: "Parish Super Administrator", finance_admin: "Parish Finance Administrator", gkk_treasurer: "GKK Treasurer",
  unit_treasurer: "Unit Treasurer / Collector", gkk_officer: "GKK President / Officer", gkk_officer_treasurer: "GKK Officer & Treasurer", member: "GKK Member", auditor: "Auditor (read-only)",
  gkk_auditor: "GKK Auditor", unit_officer: "Unit President / Coordinator", unit_auditor: "Unit Auditor",
  pec_president: "PEC President (view only)", pec_secretary: "PEC Secretary (view only)", pec_treasurer: "PEC Treasurer (view only)",
  pec_auditor: "PEC Auditor (view only)", pec_member: "PEC Member (view only)", parish_cashier: "Parish Cashier (view only)",
  internal_auditor: "Internal Auditor (view only)", external_auditor: "External Auditor (view only)",
};
export const VIEWER_ROLES = ["auditor", "pec_president", "pec_secretary", "pec_treasurer", "pec_auditor", "pec_member", "parish_cashier", "internal_auditor", "external_auditor"];
export const GLOBAL_ROLES = ["super_admin", "finance_admin", ...VIEWER_ROLES];
